import {
  KalturaMeta,
  KalturaMetaFilter,
  KalturaMetaListResponse,
  MetaAddAction,
  MetaDeleteAction,
  MetaListAction,
  MetaAddActionArgs,
  KalturaMetaArgs,
  MetaUpdateAction,
  KalturaStringValue,
} from '@kaltura-ott/ott-client-types/api/types';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { kalturaClient } from '../kaltura-clent';
import { KUX_FEATURE_NAME } from '../utils/constants';
import { filterByKUXPrefix } from '../utils/filters';

import { WIDGET_KUX_METAS } from './config';

const addFeatures = (originFeatures: string | undefined, addFeature: string): string => {
  let res;
  if (originFeatures) {
    res = originFeatures.split(',');
    res.push(addFeature);
    return res.join(',');
  } else {
    return addFeature;
  }
};
/**
 * Create meta as attribute for structs
 * In 'features' will be added KUX_FEATURE_NAME => 'kuxmanaged' prefix as an identifier
 *
 * @param data
 */
export const createMeta = (data: KalturaMetaArgs): Observable<KalturaMeta> => {
  const _data: MetaAddActionArgs = {
    meta: new KalturaMeta(data),
  };

  if (_data.meta.features.indexOf(KUX_FEATURE_NAME) === -1) {
    _data.meta.features = addFeatures(_data.meta.features, KUX_FEATURE_NAME);
  }
  const client = kalturaClient();
  client.appendDefaultRequestOptions({ language: '*' });
  const action = new MetaAddAction(_data);
  return client.request(action);
};

/**
 * Load list of all Metas, by default filtered with KUX_FEATURE_NAME in 'features'
 * @category Creator
 * @param filter: KalturaMetaFilter
 * @param withFilterByKUX: boolean
 */
export const getMetasList = (
  filter?: KalturaMetaFilter,
  withFilterByKUX = true,
): Observable<KalturaMetaListResponse> => {
  const client = kalturaClient();
  const action = new MetaListAction({ filter });
  return client.request(action).pipe(
    map((data) => (withFilterByKUX ? filterByKUXPrefix(data) : data)),
    catchError((err) => {
      return of(err);
    }),
  );
};

/**
 * Updates all dynamic data in all metas to contract values.
 * If meta is not in contract, it's not updated.
 * If meta in contract, but has no dynamic data in contract, it's not updated
 * @category Creator
 */
export const syncMetasDynamicData = () => {
  return getMetasList().pipe(
    mergeMap((metas) => {
      const metasToSync = metas.objects.filter((meta) => {
        const metaInContract = WIDGET_KUX_METAS[meta.systemName];
        const hasDynamicData = metaInContract?.dynamicData;
        return !!metaInContract && hasDynamicData;
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updateRequests = metasToSync.map((meta: any) => {
        const metaInContract = WIDGET_KUX_METAS[meta.systemName];
        if (!meta.dynamicData) {
          meta.dynamicData = {};
        }
        const dynamicKeys = metaInContract.dynamicData && Object.keys(metaInContract.dynamicData);
        dynamicKeys?.forEach((key) => {
          meta.dynamicData[key] = new KalturaStringValue({ value: metaInContract.dynamicData?.key?.toString() });
        });

        delete meta.systemName;
        delete meta.dataType;
        delete meta.isProtected;
        delete meta.parentId;

        return new MetaUpdateAction({ id: parseInt(meta.id, 10), meta });
      });

      const client = kalturaClient();
      return client.multiRequest(updateRequests).pipe(
        map((resp) => {
          if (resp.hasErrors()) {
            throw resp.getFirstError();
          }
          return resp;
        }),
      );
    }),
    catchError((err) => {
      return of(err);
    }),
  );
};

/**
 * Delete Meta/Attributes by ID
 * @param id
 */
export const deleteMeta = (id: number) => {
  const client = kalturaClient();
  const action = new MetaDeleteAction({ id });
  return client.request(action).pipe(
    catchError((err) => {
      return of(err);
    }),
  );
};

/**
 * Update Meta/Attributes by ID
 * @param id
 * @param meta
 */
export const updateMeta = (id: number, data: KalturaMetaArgs) => {
  const client = kalturaClient();
  const action = new MetaUpdateAction({ id, meta: new KalturaMeta(data) });
  return client.request(action).pipe(
    catchError((err) => {
      return of(err);
    }),
  );
};
